import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import { applyShadowToken, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectProgressStyles = _ref => {
  let {
    backgroundColor,
    borderWidth,
    borderColor,
    borderRadius,
    height,
    shadow
  } = _ref;
  return {
    backgroundColor,
    borderWidth,
    borderColor,
    borderRadius,
    height,
    ...applyShadowToken(shadow)
  };
};

/**
 * The `Progress` is a container for displaying one or several `ProgressBar`s.
 *
 * ## Props
 *
 * - Use `children` prop to pass one or multiple `ProgressBar`s. Note that in case of stacked progress bars
 *   each next will be displayed on top of the previous one.
 *
 * ## Tokens
 *
 * - Use the following tokens to customize the appearance:
 *   - `backgroundColor` for the background color of the progress container,
 *   - `borderColor` to control the color of the border,
 *   - `borderRadius` for the rounded corners,
 *   - `borderWidth` to change the border width.
 *   - `height` to control the height of the progress bars displayed within the progress bar container.
 *   - `shadow` to apply a box shadow to the progress bar container.
 *
 * ## Variants
 *
 * - Use the `size` variant to control the height of your progress bars:  passing `'mini'` will make your
 *   progress bar container narrower.
 *
 * ## Usability and A11y guidelines
 *
 * `Progress` component accepts the standard accessibility properties, however it's more important to make sure
 * its children (instances of `ProgressBar`) receive the accessibility properties specific to the `progressbar`
 * role.
 *
 */
const Progress = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children,
    tokens,
    variant,
    ...rest
  } = _ref2;
  const themeTokens = useThemeTokens('Progress', tokens, variant);
  return /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: [staticStyles.progressContainer, selectProgressStyles(themeTokens)],
    ...selectProps(rest),
    children: children
  });
});
Progress.displayName = 'Progress';
Progress.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Use the `children` prop to supply one or several `ProgressBar`s.
   */
  children: PropTypes.node.isRequired,
  /**
   * Progress tokens.
   */
  tokens: getTokensPropType('Progress'),
  /**
   * Progress variant.
   */
  variant: variantProp.propType
};
export default Progress;
const staticStyles = StyleSheet.create({
  progressContainer: {
    position: 'relative'
  }
});